import { useState, useEffect } from 'react';
import { THEME } from '../config';

/**
 * Build a `min-width` media query from a named breakpoint. Pulls the pixel value from the
 * single source of truth (`THEME.breakpoints`, which mirrors the Tailwind config), so JS
 * layout logic never hardcodes a width that could silently drift from the `md:`/`lg:` classes.
 *
 * Example: `minWidthQuery('md')` → `'(min-width: 768px)'`.
 */
export const minWidthQuery = (breakpoint: keyof typeof THEME.breakpoints): string =>
  `(min-width: ${THEME.breakpoints[breakpoint]})`;

/**
 * Reactive `md`+ check — the JS mirror of Tailwind's `md:` utilities. Use this instead of a
 * raw `useMediaQuery('(min-width: 768px)')` so every "is desktop?" test shares one breakpoint.
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(minWidthQuery('md'));
}

/**
 * Reactive media-query hook. Returns whether `query` currently matches and updates on change
 * (e.g. resize, rotation). SSR-safe: returns false until mounted.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
